/**
 * SEO Schema Markup Utilities for Directory Website
 * Comprehensive structured data for maximum search visibility
 */

import { getSiteBaseUrl } from '../config/site';

const SITE_NAME = 'Visibeen Directory';

const getSiteUrl = () => getSiteBaseUrl();

/**
 * Website Schema - Site-wide search and identity
 */
export const generateWebsiteSchema = () => ({
  '@context': 'https://schema.org',

  '@type': 'WebSite',
  '@id': `${getSiteUrl()}/#website`,
  name: SITE_NAME,
  alternateName: 'Visibeen Business Directory India',
  url: getSiteUrl(),
  description: 'Find trusted local businesses, services and professionals in your area',
  publisher: {
    '@id': `${getSiteUrl()}/#organization`
  },
  inLanguage: 'en-IN',
  potentialAction: {
    '@type': 'SearchAction',
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': `${getSiteUrl()}/search?q={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  }
});

/**
 * Organization Schema - Publisher/Owner identity
 */
export const generateOrganizationSchema = (orgData = {}) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${getSiteUrl()}/#organization`,
    name: orgData.name || SITE_NAME,
    url: getSiteUrl(),
    logo: {
      '@type': 'ImageObject',
      '@id': `${getSiteUrl()}/#logo`,
      url: orgData.logo || `${getSiteUrl()}/logo.png`,
      name: `${orgData.name || SITE_NAME} Logo`,
      width: 512,
      height: 512
    }
  };

  // Only add sameAs if we have valid social profiles
  const validSocial = (orgData.socialProfiles || []).filter(
    url => url && typeof url === 'string' && url.startsWith('http')
  );
  if (validSocial.length > 0) {
    schema.sameAs = validSocial;
  }

  // Only add contactPoint if we have a phone number
  if (orgData.phone) {
    schema.contactPoint = {
      '@type': 'ContactPoint',
      telephone: orgData.phone,
      contactType: 'customer service',
      availableLanguage: 'English'
    };
  }

  return schema;
};

/**
 * BreadcrumbList Schema - Navigation hierarchy
 */
export const generateBreadcrumbSchema = (breadcrumbs = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': `${getSiteUrl()}/#breadcrumb`,
  itemListElement: breadcrumbs.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

/**
 * CollectionPage Schema - For State/City/Category listing pages
 */
export const generateCollectionPageSchema = (pageData = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${pageData.url}/#webpage`,
  url: pageData.url,
  name: pageData.title,
  description: pageData.description,
  isPartOf: { '@id': `${getSiteUrl()}/#website` },
  breadcrumb: { '@id': `${getSiteUrl()}/#breadcrumb` },
  about: {
    '@type': 'Place',
    name: pageData.locationName,
    address: {
      '@type': 'PostalAddress',
      addressLocality: pageData.city || '',
      addressRegion: pageData.state || '',
      addressCountry: pageData.country || 'IN'
    }
  },
  mainEntity: {
    '@type': 'ItemList',
    '@id': `${pageData.url}/#itemlist`,
    numberOfItems: pageData.totalItems || 0,
    itemListElement: (pageData.items || []).slice(0, 10).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: item.url
    }))
  }
});

/**
 * ItemList Schema - For directory listings
 */
export const generateItemListSchema = (listData = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  '@id': `${listData.url || getSiteUrl()}/#itemlist`,
  name: listData.name || 'Business Directory Listings',
  description: listData.description || '',
  numberOfItems: listData.items?.length || 0,
  itemListOrder: 'https://schema.org/ItemListOrderDescending',
  itemListElement: (listData.items || []).map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'LocalBusiness',
      '@id': item.url,
      name: item.name,
      url: item.url,
      image: item.image || '',
      address: {
        '@type': 'PostalAddress',
        streetAddress: item.address || '',
        addressLocality: item.city || '',
        addressRegion: item.state || '',
        postalCode: item.postalCode || '',
        addressCountry: item.country || 'IN'
      },
      ...(item.rating && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: item.rating,
          reviewCount: item.reviewCount || 0,
          bestRating: 5,
          worstRating: 1
        }
      }),
      ...(item.category && { category: item.category })
    }
  }))
});

/**
 * LocalBusiness Schema - Individual business page (MOST IMPORTANT FOR SEO)
 * Only generates schema with valid, non-generic data
 */
export const generateLocalBusinessSchema = (business = {}) => {
  // Must have a real business name
  if (!business.name || business.name === 'Local Business') {
    return null;
  }

  const businessName = business.name;
  const businessUrl = business.url || business.website || '';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': business.businessType || 'LocalBusiness',
    name: businessName
  };

  // Only add URL if valid
  if (businessUrl && businessUrl.startsWith('http')) {
    schema['@id'] = `${businessUrl}#business`;
    schema.url = businessUrl;
  }

  // Only add description if it's meaningful (not generic)
  if (business.description && business.description.length > 20) {
    schema.description = business.description;
  }

  // Add images - only valid HTTP URLs
  const validImages = (business.images || []).filter(img => 
    img && typeof img === 'string' && img.startsWith('http')
  );
  
  if (validImages.length > 0) {
    schema.image = validImages;
  }

  // Add logo only if valid URL
  if (business.logo && business.logo.startsWith('http')) {
    schema.logo = business.logo;
  }

  // Contact info - only if present
  if (business.phone && business.phone.trim()) {
    schema.telephone = business.phone;
  }
  if (business.email && business.email.includes('@')) {
    schema.email = business.email;
  }

  // Price range
  if (business.priceRange) {
    schema.priceRange = business.priceRange;
  }

  // Address - only add if we have meaningful address data
  if (business.streetAddress && business.streetAddress.length > 5) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: business.streetAddress
    };
    if (business.city) schema.address.addressLocality = business.city;
    if (business.state) schema.address.addressRegion = business.state;
    if (business.postalCode) schema.address.postalCode = business.postalCode;
    schema.address.addressCountry = 'IN';
  }

  // Geo coordinates - only if valid numbers
  const lat = parseFloat(business.latitude);
  const lng = parseFloat(business.longitude);
  if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: lat,
      longitude: lng
    };
    schema.hasMap = `https://www.google.com/maps?q=${lat},${lng}`;
  }

  // Opening Hours - only if valid data
  if (business.openingHours && business.openingHours.length > 0) {
    const validHours = business.openingHours.filter(h => h.days && h.opens && h.closes);
    if (validHours.length > 0) {
      schema.openingHoursSpecification = validHours.map(hours => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: hours.days,
        opens: hours.opens,
        closes: hours.closes
      }));
    }
  }

  // Aggregate Rating - only with valid reviews
  const validReviews = (business.reviews || []).filter(r => 
    r && r.text && r.text.trim().length > 10 && r.authorName
  );
  
  if (validReviews.length > 0) {
    // Calculate average rating
    const ratings = validReviews.filter(r => r.rating).map(r => parseFloat(r.rating));
    const avgRating = ratings.length > 0 
      ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
      : business.rating;

    if (avgRating) {
      schema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: avgRating,
        reviewCount: validReviews.length,
        bestRating: 5,
        worstRating: 1
      };
    }

    // Add individual reviews
    schema.review = validReviews.slice(0, 5).map(review => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.authorName
      },
      datePublished: review.date || new Date().toISOString().split('T')[0],
      reviewBody: review.text,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating || 5,
        bestRating: 5,
        worstRating: 1
      }
    }));
  }

  // Services - simplified, no nested provider
  const validServices = (business.services || []).filter(s => s.name && s.name.trim());
  if (validServices.length > 0) {
    schema.makesOffer = validServices.map(service => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service.name,
        description: service.description || undefined
      }
    }));
  }

  // Social Profiles
  const validSocialProfiles = (business.socialProfiles || []).filter(
    url => url && typeof url === 'string' && url.startsWith('http')
  );
  if (validSocialProfiles.length > 0) {
    schema.sameAs = validSocialProfiles;
  }

  // Payment Methods
  if (business.paymentAccepted && Array.isArray(business.paymentAccepted) && business.paymentAccepted.length > 0) {
    schema.paymentAccepted = business.paymentAccepted.join(', ');
  }

  return schema;
};

/**
 * FAQPage Schema - For FAQ sections
 */
export const generateFAQSchema = (faqs = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

/**
 * Product/Service Schema - For service-based businesses
 */
export const generateServiceSchema = (service = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${service.url}/#service`,
  name: service.name,
  description: service.description,
  provider: {
    '@type': 'LocalBusiness',
    name: service.providerName,
    '@id': service.providerUrl
  },
  areaServed: {
    '@type': 'Place',
    name: service.areaServed
  },
  ...(service.price && {
    offers: {
      '@type': 'Offer',
      price: service.price,
      priceCurrency: service.currency || 'INR'
    }
  })
});

/**
 * Event Schema - For business events
 */
export const generateEventSchema = (event = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: event.name,
  description: event.description,
  startDate: event.startDate,
  endDate: event.endDate,
  location: {
    '@type': 'Place',
    name: event.locationName,
    address: {
      '@type': 'PostalAddress',
      streetAddress: event.streetAddress,
      addressLocality: event.city,
      addressRegion: event.state,
      addressCountry: event.country || 'IN'
    }
  },
  organizer: {
    '@type': 'Organization',
    name: event.organizerName,
    url: event.organizerUrl
  },
  ...(event.image && { image: event.image }),
  ...(event.offers && {
    offers: {
      '@type': 'Offer',
      price: event.price || '0',
      priceCurrency: event.currency || 'INR',
      availability: 'https://schema.org/InStock',
      url: event.ticketUrl
    }
  })
});

/**
 * State/Region Page Schema
 */
export const generateStatePageSchema = (stateData = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${stateData.url}/#webpage`,
  url: stateData.url,
  name: `Best Businesses in ${stateData.stateName} | ${SITE_NAME}`,
  description: `Discover top-rated local businesses, services and professionals in ${stateData.stateName}. Browse ${stateData.totalBusinesses || 0}+ verified listings.`,
  isPartOf: { '@id': `${getSiteUrl()}/#website` },
  about: {
    '@type': 'AdministrativeArea',
    '@id': `${stateData.url}/#place`,
    name: stateData.stateName,
    containedInPlace: {
      '@type': 'Country',
      name: stateData.country || 'India'
    }
  },
  mainEntity: {
    '@type': 'ItemList',
    name: `Cities in ${stateData.stateName}`,
    numberOfItems: stateData.cities?.length || 0,
    itemListElement: (stateData.cities || []).map((city, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'City',
        name: city.name,
        url: city.url
      }
    }))
  }
});

/**
 * City Page Schema
 */
export const generateCityPageSchema = (cityData = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${cityData.url}/#webpage`,
  url: cityData.url,
  name: `Best Businesses in ${cityData.cityName}, ${cityData.stateName} | ${SITE_NAME}`,
  description: `Find ${cityData.totalBusinesses || 0}+ trusted local businesses in ${cityData.cityName}, ${cityData.stateName}. Browse reviews, ratings and contact information.`,
  isPartOf: { '@id': `${getSiteUrl()}/#website` },
  about: {
    '@type': 'City',
    '@id': `${cityData.url}/#place`,
    name: cityData.cityName,
    containedInPlace: {
      '@type': 'AdministrativeArea',
      name: cityData.stateName
    }
  },
  mainEntity: {
    '@type': 'ItemList',
    name: `Business Categories in ${cityData.cityName}`,
    numberOfItems: cityData.categories?.length || 0,
    itemListElement: (cityData.categories || []).map((cat, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: cat.name,
      url: cat.url
    }))
  }
});

/**
 * Category Page Schema
 */
export const generateCategoryPageSchema = (categoryData = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${categoryData.url}/#webpage`,
  url: categoryData.url,
  name: `Best ${categoryData.categoryName} in ${categoryData.cityName}, ${categoryData.stateName} | ${SITE_NAME}`,
  description: `Top ${categoryData.totalBusinesses || 0} ${categoryData.categoryName} services in ${categoryData.cityName}. Compare ratings, reviews and get contact details.`,
  isPartOf: { '@id': `${getSiteUrl()}/#website` },
  about: {
    '@type': 'Service',
    name: categoryData.categoryName,
    areaServed: {
      '@type': 'City',
      name: categoryData.cityName
    }
  },
  mainEntity: {
    '@type': 'ItemList',
    name: `${categoryData.categoryName} in ${categoryData.cityName}`,
    numberOfItems: categoryData.businesses?.length || 0,
    itemListElement: (categoryData.businesses || []).slice(0, 10).map((biz, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'LocalBusiness',
        name: biz.name,
        url: biz.url,
        image: biz.image,
        telephone: biz.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: biz.address,
          addressLocality: categoryData.cityName,
          addressRegion: categoryData.stateName
        },
        ...(biz.rating && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: biz.rating,
            reviewCount: biz.reviewCount || 0
          }
        })
      }
    }))
  }
});

/**
 * Combined Schema Graph - All schemas in one JSON-LD
 */
export const generateSchemaGraph = (schemas = []) => ({
  '@context': 'https://schema.org',
  '@graph': schemas
});

/**
 * Helper: Inject schema into document head
 */
export const injectSchema = (schema) => {
  const existingScript = document.querySelector('script[data-schema="directory"]');
  if (existingScript) {
    existingScript.remove();
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema', 'directory');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};

/**
 * Helper: Remove all injected schemas
 */
export const removeSchema = () => {
  const scripts = document.querySelectorAll('script[data-schema="directory"]');
  scripts.forEach(script => script.remove());
};

export default {
  generateWebsiteSchema,
  generateOrganizationSchema,
  generateBreadcrumbSchema,
  generateCollectionPageSchema,
  generateItemListSchema,
  generateLocalBusinessSchema,
  generateFAQSchema,
  generateServiceSchema,
  generateEventSchema,
  generateStatePageSchema,
  generateCityPageSchema,
  generateCategoryPageSchema,
  generateSchemaGraph,
  injectSchema,
  removeSchema
};
